import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { PreSaveTransactionSummary } from './components/PreSaveTransactionSummary';
import { SaveProgress } from './components/SaveProgress';
import { SaveStatus } from './components/SaveStatus';
import { ErrorState } from './components/ErrorState';
import { LoadingState } from './components/LoadingState';
import type { TransactionSummary } from './types';

export function TransactionList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<TransactionSummary>({
    totalTransactions: 0, totalAmount: 0,
    totalSiteFees: 0, totalBankFees: 0, totalResellerFees: 0,
    approvedTransactions: 0, declinedTransactions: 0, returnedTransactions: 0
  });
  const [showSummary, setShowSummary] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingText, setLoadingText] = useState('Loading data...');
  const [progress, setProgress] = useState<{ 
    current: number; 
    total: number;
  } | null>(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sid = searchParams.get('sid');
  const dateRange = searchParams.get('dateRange');
  const [siteName, setSiteName] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [queryDateRange, setQueryDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: null,
    end: null
  });
  const [saved, setSaved] = useState(false);
  const [saveProgress, setSaveProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [actualDateRange, setActualDateRange] = useState<{
    firstTransaction: Date | null;
    lastTransaction: Date | null;
  }>({
    firstTransaction: null,
    lastTransaction: null
  });

  useEffect(() => {
    if (sid) {
      setLoading(true);
      setError(null);
      fetchSiteName();
      fetchTransactions();
    }
  }, [sid]);

  const fetchSiteName = async () => {
    try {
      const response = await fetch('/api/sites');
      if (!response.ok) {
        throw new Error('Failed to fetch sites');
      }
      const sites = await response.json();
      const site = sites.find((s: { sid: number }) => s.sid === parseInt(sid!));
      if (site) {
        setSiteName(site.name);
      }
    } catch (err) {
      console.error('Error fetching site name:', err);
    }
  };

  const parseDateRange = (): { start: Date; end: Date } | null => {
    const now = new Date();
    let start: Date;
    let end: Date;

    if (dateRange === '3days') {
      end = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
      end.setUTCHours(23, 59, 59, 999);
      start = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);
      start.setUTCHours(0, 0, 0, 0);
    } else if (dateRange === '1month') {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start.setUTCHours(0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
      end.setUTCHours(23, 59, 59, 999);
    } else if (dateRange === 'custom') {
      start = new Date(searchParams.get('startDate')!);
      start.setUTCHours(0, 0, 0, 0);
      end = new Date(searchParams.get('endDate')!);
      end.setUTCHours(23, 59, 59, 999);
    } else {
      return null;
    }

    return { start, end };
  };

  const fetchTransactions = async () => {
    if (!sid || !dateRange) return;

    try {
      const range = parseDateRange();
      if (!range) return;
      const { start, end } = range;

      setQueryDateRange({ start, end });
      setLoadingText('Loading transaction summary...');

      const response = await fetch(
        `/api/v1/transactions/summary?sid=${sid}&start_date=${start.toISOString()}&end_date=${end.toISOString()}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch summary: ${response.status}`);
      }

      const data = await response.json();

      setSummary({
        totalTransactions: data.totalTransactions,
        totalAmount: data.totalAmount,
        totalSiteFees: data.totalSiteFees,
        totalBankFees: data.totalBankFees,
        totalResellerFees: data.totalResellerFees,
        approvedTransactions: data.approvedTransactions,
        declinedTransactions: data.declinedTransactions,
        returnedTransactions: data.returnedTransactions,
      });

      if (data.firstTransaction) {
        setActualDateRange({
          firstTransaction: new Date(data.firstTransaction),
          lastTransaction: new Date(data.lastTransaction),
        });
      }

      if (data.totalTransactions === 0) {
        setError('No transactions found for the selected period');
      } else {
        setShowSummary(true);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load transactions');
      setLoading(false);
    }
  };

  const saveDataset = async () => {
    if (!sid || !dateRange || summary.totalTransactions === 0 || !siteName) return;

    setSaving(true);
    setSaveError(null);

    try {
      if (!actualDateRange.firstTransaction || !actualDateRange.lastTransaction) {
        throw new Error('No transactions found to determine date range');
      }

      const { data: dataset, error: datasetError } = await supabase
        .from('datasets')
        .insert({
          name: siteName || `Dataset ${new Date().toLocaleDateString()}`,
          site_id: parseInt(sid),
          site_name: siteName || '',
          date_range: dateRange,
          start_date: actualDateRange.firstTransaction,
          end_date: actualDateRange.lastTransaction,
          total_transactions: summary.totalTransactions,
          total_volume: summary.totalAmount,
          total_site_fees: summary.totalSiteFees,
          total_bank_fees: summary.totalBankFees,
          total_reseller_fees: summary.totalResellerFees,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .select()
        .single();

      if (datasetError) throw datasetError;
      if (!dataset) throw new Error('Failed to create dataset');

      const range = parseDateRange();
      if (!range) throw new Error('Invalid date range');

      const streamResponse = await fetch(
        `/api/v1/transactions/stream?sid=${sid}&start_date=${range.start.toISOString()}&end_date=${range.end.toISOString()}`
      );

      if (!streamResponse.ok || !streamResponse.body) {
        throw new Error(`Stream failed: ${streamResponse.status}`);
      }

      const totalFromHeader = parseInt(streamResponse.headers.get('X-Total-Count') || '0', 10);
      setSaveProgress({ current: 0, total: totalFromHeader || summary.totalTransactions });
      setLoadingText('Streaming and saving transactions...');

      const reader = streamResponse.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let batch: Record<string, unknown>[] = [];
      let saved = 0;
      const BATCH_SIZE = 1000;

      const flushBatch = async () => {
        if (batch.length === 0) return;
        const { error: insertError } = await supabase
          .from('dataset_transactions')
          .insert(batch);
        if (insertError) throw insertError;
        saved += batch.length;
        setSaveProgress({ current: saved, total: totalFromHeader || summary.totalTransactions });
        setLoadingText(`Saving transactions (${saved.toLocaleString()} of ${(totalFromHeader || summary.totalTransactions).toLocaleString()})`);
        batch = [];
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;
          const parsed = JSON.parse(line);
          if (parsed.meta) continue;

          batch.push({
            dataset_id: dataset.id,
            transaction_id: String(parsed.id),
            amount: Number(parsed.amount),
            status: parsed.status,
            timestamp: parsed.timestamp,
            bank: parsed.bank,
            method: parsed.method,
            type: parsed.type,
            site_fee_flat: parsed.site_fee_flat ?? 0,
            site_fee_rate: parsed.site_fee_rate ?? 0,
            bank_fee_flat: parsed.bank_fee_flat ?? 0,
            bank_fee_rate: parsed.bank_fee_rate ?? 0,
            reseller_fee_flat: parsed.reseller_fee_flat ?? 0,
            reseller_fee_rate: parsed.reseller_fee_rate ?? 0,
          });

          if (batch.length >= BATCH_SIZE) {
            await flushBatch();
          }
        }
      }

      if (buffer.trim()) {
        const parsed = JSON.parse(buffer);
        if (!parsed.meta) {
          batch.push({
            dataset_id: dataset.id,
            transaction_id: String(parsed.id),
            amount: Number(parsed.amount),
            status: parsed.status,
            timestamp: parsed.timestamp,
            bank: parsed.bank,
            method: parsed.method,
            type: parsed.type,
            site_fee_flat: parsed.site_fee_flat ?? 0,
            site_fee_rate: parsed.site_fee_rate ?? 0,
            bank_fee_flat: parsed.bank_fee_flat ?? 0,
            bank_fee_rate: parsed.bank_fee_rate ?? 0,
            reseller_fee_flat: parsed.reseller_fee_flat ?? 0,
            reseller_fee_rate: parsed.reseller_fee_rate ?? 0,
          });
        }
      }
      await flushBatch();

      setSaved(true);
      setTimeout(() => {
        navigate('/datasets');
      }, 1500);
    } catch (err) {
      console.error('Error saving dataset:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to save dataset');
    } finally {
      setSaving(false);
      setSaveProgress(null);
    }
  };

  if (loading) {
    return <LoadingState loadingText={loadingText} progress={progress} />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 space-y-4">
        <Link
          to="/datasets/new"
          className="flex items-center text-sm text-indigo-600 hover:text-indigo-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Datasets
        </Link>
        {!saved && summary.totalTransactions > 0 && (
          <button
            onClick={saveDataset}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 min-w-[120px]"
          >
            <SaveStatus
              saving={saving}
              saved={saved}
              saveError={saveError}
              loadingText={loadingText}
            />
          </button>
        )}
        {saveProgress && <SaveProgress current={saveProgress.current} total={saveProgress.total} />}
      </div>

      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        {error ? (
          <ErrorState error={error} />
        ) : !showSummary ? (
          <div className="p-4 text-center text-gray-500">
            No transactions found for the selected period.
          </div>
        ) : (
          <>
            <PreSaveTransactionSummary 
              stats={summary || {
                totalTransactions: 0,
                totalAmount: 0,
                totalSiteFees: 0,
                totalBankFees: 0,
                totalResellerFees: 0,
                approvedTransactions: 0,
                declinedTransactions: 0,
                returnedTransactions: 0
              }}
              dateRange={dateRange || '3days'}
              queryDateRange={queryDateRange.start && queryDateRange.end ? {
                start: queryDateRange.start,
                end: queryDateRange.end
              } : undefined}
            />
          </>
        )}
      </div>
    </div>
  );
}